<template>
  <aside class="xp-support" data-recording-sensitive="true" data-sl="mask">
    <section v-if="open" id="xp-support-panel" class="xp-support__panel" role="dialog" :aria-label="copy.title" @keydown.esc="close" @keydown="trapFocus">
      <header class="xp-support__header">
        <div class="xp-support__avatar" aria-hidden="true">x</div>
        <div><h2>{{ copy.title }}</h2><p>{{ copy.subtitle }}</p></div>
        <button type="button" class="xp-support__icon" :aria-label="copy.close" @click="close">×</button>
      </header>
      <div v-if="!user" class="xp-support__welcome">
        <h3>{{ copy.visitorTitle }}</h3>
        <p>{{ copy.visitorText }}</p>
        <a href="mailto:support@app-xprofit.com" class="xp-support__primary">{{ copy.emailUs }}</a>
        <router-link to="/help" @click.native="close">{{ copy.helpCenter }}</router-link>
      </div>
      <template v-else>
        <div class="xp-support__context"><span class="xp-support__dot" />{{ user.uid }}</div>
        <div ref="messages" class="xp-support__messages" role="log" aria-live="polite" :aria-busy="busy">
          <div v-if="!messages.length" class="xp-support__welcome">
            <span class="xp-support__eyebrow">{{ aiEnabled ? 'XPROFIT ASSISTANT' : 'XPROFIT SUPPORT' }}</span>
            <h3>{{ copy.welcome }}</h3>
            <p>{{ copy.intro }}</p>
            <button v-for="suggestion in copy.suggestions" :key="suggestion" type="button" class="xp-support__suggestion" :disabled="busy" @click="chooseSuggestion(suggestion)">{{ suggestion }} <span aria-hidden="true">↗</span></button>
          </div>
          <article v-for="message in messages" :key="message.id" :class="['xp-support__message', { 'xp-support__message--user': message.role === 'user' }]">
            <span class="xp-support__author">{{ message.role === 'user' ? copy.you : 'xProfit Support' }}</span>
            <p>{{ message.text }}</p>
          </article>
          <p v-if="loading" class="xp-support__status">{{ copy.loading }}</p>
          <p v-else-if="sending" class="xp-support__status">{{ copy.thinking }}</p>
        </div>
        <div v-if="conversation && conversation.needsHuman" class="xp-support__handoff" role="status">
          {{ copy.handoff }} <span v-if="conversation.contactEmail">{{ conversation.contactEmail }}</span>
        </div>
        <div v-if="error" class="xp-support__error" role="alert">
          {{ error }}
          <button v-if="retryPayload" type="button" :disabled="busy" @click="retry">{{ copy.retry }}</button>
          <button v-else-if="loadFailed" type="button" :disabled="busy" @click="load">{{ copy.retry }}</button>
        </div>
        <form class="xp-support__composer" @submit.prevent="send">
          <label v-if="!conversation" class="xp-support__email">
            {{ copy.replyEmail }}
            <input v-model.trim="contactEmail" type="email" maxlength="254" autocomplete="email" :placeholder="copy.emailPlaceholder" :disabled="busy || !!retryPayload" />
          </label>
          <label class="xp-support__sr-only" for="xp-support-message">{{ copy.placeholder }}</label>
          <div class="xp-support__input-row">
            <textarea id="xp-support-message" ref="input" v-model="draft" rows="2" maxlength="2000" :placeholder="copy.placeholder" :disabled="busy || !!retryPayload || !!(conversation && conversation.full)" @keydown.enter.exact.prevent="send" @keydown.enter.shift.exact.stop />
            <button type="submit" class="xp-support__send" :disabled="busy || !draft.trim() || !!retryPayload || !!(conversation && conversation.full)" :aria-label="copy.send">↑</button>
          </div>
          <p class="xp-support__privacy">{{ copy.privacy }} <router-link to="/privacy">{{ copy.privacyLink }}</router-link></p>
        </form>
        <footer class="xp-support__footer">
          <button type="button" :disabled="busy || !!retryPayload || !conversation || conversation.needsHuman" @click="handoff">{{ copy.contactTeam }}</button>
          <button type="button" :disabled="busy || !!retryPayload" @click="startNew">{{ copy.newChat }}</button>
          <router-link to="/help" @click.native="close">{{ copy.helpCenter }}</router-link>
        </footer>
        <p v-if="conversation && conversation.full" class="xp-support__status">{{ copy.full }}</p>
      </template>
    </section>
    <button ref="launcher" type="button" class="xp-support__launcher" :aria-expanded="String(open)" aria-controls="xp-support-panel" :aria-label="open ? copy.close : copy.title" @click="toggle">
      <svg v-if="!open" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M20 11.5a8 8 0 0 1-8 8H5l-3 2v-10a9 9 0 0 1 18 0Z" /><path d="M7 10h8M7 14h5" /></svg>
      <span v-else aria-hidden="true">×</span><span>{{ open ? copy.close : 'Support' }}</span>
    </button>
  </aside>
</template>

<script>
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { supportId, supportRequest } from '@/services/support';

const COPY = {
  en: {
    title: 'xProfit Support', subtitle: 'AI assistant · Human help when needed', close: 'Close', welcome: 'How can we help?',
    intro: 'Ask about your figures, integrations or settings. Your shop is already connected.',
    suggestions: ['Help me understand my profit', 'My PayPal figures look different', 'How do I set up product costs?'],
    visitorTitle: 'Let’s get you some help.', visitorText: 'Open xProfit from your Shopify store to chat with your shop connected. You can also email our team or visit the Help Center.',
    emailUs: 'Email support', helpCenter: 'Help Center', you: 'You', loading: 'Loading your conversation…', thinking: 'Working on your message…',
    handoff: 'Saved for the team. Human replies arrive by email:', retry: 'Try again', replyEmail: 'Reply email (optional if already saved in your shop)', emailPlaceholder: 'you@yourshop.com',
    placeholder: 'Ask about xProfit…', send: 'Send message', privacy: 'AI can make mistakes. Your message is processed by OpenAI and emailed to our team. Don’t share passwords or customer data.', privacyLink: 'Privacy',
    contactTeam: 'Contact the team', newChat: 'New chat', full: 'This conversation is full. Start a new chat or email support@app-xprofit.com.',
    unavailable: 'We could not confirm the response. Try again to check the same message, or email support@app-xprofit.com.',
    pending: 'Your message is still processing. Try again in a moment.',
  },
  de: {
    title: 'xProfit Support', subtitle: 'KI-Assistent · Bei Bedarf hilft unser Team', close: 'Schließen', welcome: 'Wie können wir helfen?',
    intro: 'Frag uns zu deinen Zahlen, Integrationen oder Einstellungen. Dein Shop ist bereits zugeordnet.',
    suggestions: ['Hilf mir, meinen Gewinn zu verstehen', 'Meine PayPal-Zahlen weichen ab', 'Wie richte ich Produktkosten ein?'],
    visitorTitle: 'Wir helfen dir weiter.', visitorText: 'Öffne xProfit über deinen Shopify-Shop, um mit automatischer Shop-Zuordnung zu chatten. Du kannst uns auch eine E-Mail schreiben oder das Hilfecenter nutzen.',
    emailUs: 'Support per E-Mail', helpCenter: 'Hilfecenter', you: 'Du', loading: 'Dein Verlauf wird geladen…', thinking: 'Deine Nachricht wird bearbeitet…',
    handoff: 'Für das Team gespeichert. Antworten erhältst du per E-Mail:', retry: 'Erneut versuchen', replyEmail: 'Antwort-E-Mail (optional, wenn im Shop hinterlegt)', emailPlaceholder: 'du@deinshop.de',
    placeholder: 'Deine Frage zu xProfit…', send: 'Nachricht senden', privacy: 'KI kann Fehler machen. Deine Nachricht wird von OpenAI verarbeitet und unserem Team per E-Mail zugeschickt. Teile keine Passwörter oder Kundendaten.', privacyLink: 'Datenschutz',
    contactTeam: 'Support kontaktieren', newChat: 'Neuer Chat', full: 'Dieser Verlauf ist voll. Starte einen neuen Chat oder schreibe an support@app-xprofit.com.',
    unavailable: 'Die Antwort konnte nicht bestätigt werden. Versuche es erneut, um dieselbe Nachricht zu prüfen, oder schreibe an support@app-xprofit.com.',
    pending: 'Deine Nachricht wird noch bearbeitet. Versuche es gleich erneut.',
  },
};

export default {
  name: 'SupportChat',
  data: () => ({ open: false, user: null, aiEnabled: false, loaded: false, conversation: null, conversationId: '', draft: '', contactEmail: '',
    loading: false, sending: false, error: '', retryPayload: null, loadFailed: false, epoch: 0,
    language: typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en' }),
  computed: {
    copy() {
      if (this.aiEnabled) return COPY[this.language];
      return { ...COPY[this.language], ...(this.language === 'de' ? {
        subtitle: 'Dein direkter Kontakt zum xProfit-Team',
        intro: 'Beschreibe dein Anliegen. Dein Shop wird automatisch zugeordnet, und unser Team antwortet per E-Mail.',
        thinking: 'Deine Nachricht wird gesendet…',
        privacy: 'Deine Nachricht wird gespeichert und unserem Team per E-Mail zugeschickt. Teile keine Passwörter oder Kundendaten.',
      } : {
        subtitle: 'Your direct line to the xProfit team',
        intro: 'Tell us what you need. Your shop is connected automatically, and our team will reply by email.',
        thinking: 'Sending your message…',
        privacy: 'Your message is saved and emailed to our team. Don’t share passwords or customer data.',
      }) };
    },
    busy() { return this.loading || this.sending; },
    messages() { return this.conversation?.messages || []; },
  },
  mounted() {
    this.unsubscribe = firebase.auth().onAuthStateChanged(user => {
      this.epoch += 1;
      this.user = user;
      this.reset();
      if (this.open && user) this.load();
    });
  },
  beforeDestroy() { this.epoch += 1; this.unsubscribe?.(); },
  methods: {
    reset() {
      this.conversation = null; this.conversationId = supportId(); this.draft = ''; this.contactEmail = ''; this.loaded = false; this.aiEnabled = false;
      this.error = ''; this.retryPayload = null; this.loading = false; this.sending = false; this.loadFailed = false;
    },
    async toggle() {
      if (this.open) return this.close();
      this.open = true;
      if (this.user && !this.loaded && !this.retryPayload) await this.load();
      this.$nextTick(() => (this.$refs.input || this.$el.querySelector('a, section button'))?.focus());
    },
    close() { this.open = false; this.$nextTick(() => this.$refs.launcher?.focus()); },
    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const elements = Array.from(event.currentTarget.querySelectorAll('button:not(:disabled), a, input:not(:disabled), textarea:not(:disabled)'));
      const first = elements[0]; const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    },
    scroll() { this.$nextTick(() => { if (this.$refs.messages) this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight; }); },
    async load() {
      const epoch = this.epoch; const user = this.user;
      this.loading = true; this.error = ''; this.loadFailed = false;
      try {
        const { conversation, aiEnabled } = await supportRequest({ action: 'load' }, user);
        if (epoch !== this.epoch) return;
        this.aiEnabled = aiEnabled; this.loaded = true;
        if (conversation) {
          this.conversation = conversation; this.conversationId = conversation.id;
          this.contactEmail = conversation.contactEmail || '';
          if (conversation.pending) {
            const pending = [...conversation.messages].reverse().find(message => message.role === 'user');
            this.retryPayload = { action: 'message', conversationId: conversation.id, requestId: pending.id, message: pending.text, language: this.language };
            this.error = this.copy.pending;
          }
        }
        this.scroll();
      } catch (_) { if (epoch === this.epoch) { this.error = this.copy.unavailable; this.loadFailed = true; } }
      finally { if (epoch === this.epoch) this.loading = false; }
    },
    chooseSuggestion(text) { this.draft = text; this.$refs.input?.focus(); },
    startNew() { const aiEnabled = this.aiEnabled; this.epoch += 1; this.reset(); this.aiEnabled = aiEnabled; this.loaded = true; this.$nextTick(() => this.$refs.input?.focus()); },
    send() {
      if (this.busy || this.retryPayload || !this.draft.trim() || this.conversation?.full) return;
      return this.submit({ action: 'message', message: this.draft.trim() });
    },
    handoff() { if (!this.busy && this.conversation && !this.conversation.needsHuman) return this.submit({ action: 'handoff' }); },
    retry() { if (!this.busy && this.retryPayload) return this.submit(null, this.retryPayload); },
    async submit(input, retry = null) {
      const epoch = this.epoch; const user = this.user;
      const payload = retry || { ...input, conversationId: this.conversationId, requestId: supportId(),
        contactEmail: this.contactEmail, language: this.language, page: this.$route.path };
      this.sending = true; this.error = ''; this.retryPayload = payload;
      try {
        const { conversation, aiEnabled } = await supportRequest(payload, user);
        if (epoch !== this.epoch) return;
        this.aiEnabled = aiEnabled; this.loaded = true;
        this.conversation = conversation; this.conversationId = conversation.id; this.contactEmail = conversation.contactEmail || '';
        this.retryPayload = null; this.draft = ''; this.loadFailed = false; this.scroll();
      } catch (error) {
        if (epoch !== this.epoch) return;
        this.error = error.code === 'IN_PROGRESS' ? this.copy.pending : (error.status && error.status < 500 ? error.message : this.copy.unavailable);
        // Only definitive rejections can be safely edited and resubmitted.
        if (error.status && error.status < 500 && error.code !== 'IN_PROGRESS') this.retryPayload = null;
      } finally { if (epoch === this.epoch) { this.sending = false; this.$nextTick(() => this.$refs.input?.focus()); } }
    },
  },
};
</script>

<style scoped>
.xp-support { --ink: #172c36; --teal: #087e76; font-family: inherit; color: var(--ink); position: fixed; right: max(22px, env(safe-area-inset-right)); bottom: max(22px, env(safe-area-inset-bottom)); z-index: 1050; letter-spacing: 0; }
.xp-support button, .xp-support input, .xp-support textarea { font: inherit; }
.xp-support button { cursor: pointer; }
.xp-support button:disabled { opacity: .48; cursor: not-allowed; }
.xp-support button:focus-visible, .xp-support a:focus-visible, .xp-support input:focus-visible, .xp-support textarea:focus-visible { outline: 3px solid #53bbaf; outline-offset: 3px; }
.xp-support__launcher { display: flex; align-items: center; gap: 10px; margin-left: auto; padding: 15px 21px; border: 0; border-radius: 30px; background: var(--ink); color: white; font-size: 14px; font-weight: 600; box-shadow: 0 6px 24px #142b3626; }
.xp-support__launcher > span:first-child:not(:last-child) { font-size: 23px; line-height: 20px; }
.xp-support__panel { display: flex; flex-direction: column; width: 390px; height: min(680px, calc(100dvh - 115px)); min-height: 320px; margin-bottom: 14px; background: #fff; border: 1px solid #dce7e5; border-radius: 22px; box-shadow: 0 16px 64px #162b362b; overflow: hidden; }
.xp-support__header { display: flex; align-items: center; gap: 11px; padding: 19px; background: var(--ink); color: #fff; }
.xp-support__header h2 { font-size: 16px; font-weight: 600; margin: 0 0 4px; color: white; }
.xp-support__header p { font-size: 11px; margin: 0; color: #d0e2df; }
.xp-support__avatar { display: grid; place-items: center; width: 39px; height: 39px; border-radius: 13px; background: #c3efdd; color: var(--ink); font-weight: 600; font-size: 27px; }
.xp-support__icon { margin-left: auto; color: white; border: 0; background: transparent; font-size: 25px !important; padding: 4px 6px; }
.xp-support__context { display: flex; align-items: center; gap: 7px; padding: 9px 20px; background: #f1f7f5; font-size: 11px; overflow-wrap: anywhere; }
.xp-support__dot { width: 6px; height: 6px; border-radius: 50%; background: var(--teal); flex: 0 0 6px; }
.xp-support__messages { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 21px 18px 10px; }
.xp-support__welcome { padding: 9px 2px 15px; }
.xp-support__panel > .xp-support__welcome { padding: 30px 24px; }
.xp-support__eyebrow { font-size: 9px; letter-spacing: 1.6px; color: var(--teal); font-weight: 600; }
.xp-support__welcome h3 { font-size: 23px; font-weight: 600; margin: 10px 0; color: var(--ink); }
.xp-support__welcome p { font-size: 13px; line-height: 1.65; color: #5d7079; margin: 0 0 21px; }
.xp-support__suggestion { width: 100%; text-align: left; margin-bottom: 8px; padding: 12px; border: 1px solid #dfe8e6; border-radius: 11px; color: var(--ink); background: #fff; font-size: 12px !important; display: flex; justify-content: space-between; gap: 8px; }
.xp-support__suggestion:hover { background: #f2f8f6; }
.xp-support__message { margin: 0 25px 14px 0; padding: 11px 13px; background: #f0f5f4; border-radius: 2px 13px 13px; overflow-wrap: anywhere; }
.xp-support__message--user { margin: 0 0 14px 25px; border-radius: 13px 2px 13px 13px; background: #e1f2eb; }
.xp-support__author { color: #56736d; font-size: 10px; font-weight: 600; }
.xp-support__message p { font-size: 13px; line-height: 1.6; margin: 5px 0 0; white-space: pre-wrap; }
.xp-support__status { margin: 6px 18px 12px; color: #677d82; font-size: 12px; }
.xp-support__handoff { background: #eef8f1; padding: 10px 18px; font-size: 11px; line-height: 1.5; overflow-wrap: anywhere; }
.xp-support__error { padding: 10px 18px; background: #fff4e9; color: #754720; font-size: 12px; }
.xp-support__error button { border: 0; background: none; text-decoration: underline; color: inherit; }
.xp-support__composer { padding: 10px 15px 0; border-top: 1px solid #edf1f0; }
.xp-support__email { display: block; font-size: 10px; color: #62767e; margin-bottom: 8px; }
.xp-support__email input { display: block; width: 100%; box-sizing: border-box; font-size: 12px; padding: 7px 8px; border: 1px solid #dce5e3; border-radius: 7px; margin-top: 4px; }
.xp-support__input-row { display: flex; gap: 8px; align-items: flex-end; border: 1px solid #cadbd6; border-radius: 13px; padding: 10px; }
.xp-support textarea { flex: 1; width: 0; resize: none; min-height: 40px; max-height: 100px; border: 0; background: none; color: var(--ink); font-size: 13px; line-height: 1.5; }
.xp-support__send { border: 0; background: var(--teal); color: white; border-radius: 10px; width: 34px; height: 34px; font-size: 24px !important; }
.xp-support__privacy { margin: 9px 1px; font-size: 9px; line-height: 1.5; color: #71828a; }
.xp-support a { color: var(--teal); }
.xp-support__footer { display: flex; justify-content: space-between; gap: 5px; padding: 10px 16px 13px; border-top: 1px solid #edf1f0; }
.xp-support__footer button, .xp-support__footer a { padding: 0; border: 0; background: none; color: var(--teal); font-size: 10px; }
.xp-support__primary { display: block; text-align: center; padding: 12px; margin-bottom: 18px; border-radius: 12px; background: #e1f2eb; }
.xp-support__sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
@media (max-width: 480px) {
  .xp-support { right: 12px; bottom: max(12px, env(safe-area-inset-bottom)); }
  .xp-support__panel { width: calc(100vw - 24px); height: min(690px, calc(100dvh - 100px)); margin-bottom: 10px; }
  .xp-support__launcher { padding: 12px 17px; }
  .xp-support textarea, .xp-support__email input { font-size: 16px; }
}
@media (max-height: 550px) { .xp-support__panel { min-height: 0; } .xp-support__header { padding: 10px 15px; } .xp-support__privacy { display: none; } }
</style>
